import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, X, Filter } from "lucide-react";
import { api } from "../lib/api";
import TripCard from "../components/TripCard";
import MapView from "../components/MapView";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const TYPES = [
  "Pêche au bar", "Pêche au lieu", "Pêche au maquereau", "Pêche à la seiche",
  "Pêche aux leurres", "Pêche en dérive", "Pêche côtière", "Pêche familiale",
];
const LEVELS = ["Familiale", "Tous niveaux", "Débutant accepté", "Intermédiaire", "Confirmé"];

export default function Explorer() {
  const [search, setSearch] = useSearchParams();
  const [q, setQ] = useState(search.get("q") || "");
  const [port, setPort] = useState(search.get("port") || "");
  const [type, setType] = useState(search.get("type") || "");
  const [level, setLevel] = useState(search.get("level") || "");
  const [hasSpots, setHasSpots] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => { document.title = "Sorties pêche disponibles — Le Bon Matelot"; }, []);

  const { data: ports = [] } = useQuery({
    queryKey: ["ports"], queryFn: async () => (await api.get("/ports")).data,
  });

  const params = useMemo(() => {
    const o = {};
    if (q) o.q = q;
    if (port) o.port = port;
    if (type) o.type = type;
    if (level) o.level = level;
    if (hasSpots) o.has_spots = true;
    return o;
  }, [q, port, type, level, hasSpots]);

  const { data: trips = [], isLoading } = useQuery({
    queryKey: ["trips", params],
    queryFn: async () => (await api.get("/trips", { params })).data,
  });

  const reset = () => { setQ(""); setPort(""); setType(""); setLevel(""); setHasSpots(false); setSearch({}); };

  // Group : Bréhec en premier
  const ordered = useMemo(() => {
    const bre = trips.filter((t) => t.port === "Bréhec");
    const others = trips.filter((t) => t.port !== "Bréhec");
    return [...bre, ...others];
  }, [trips]);

  return (
    <div className="bg-blanc min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-brume/40 to-blanc border-b border-brume">
        <div className="max-w-6xl mx-auto px-6 pt-10 pb-6">
          <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Sorties disponibles</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-ocean mt-1 tracking-tight">
            {trips.length} sortie{trips.length > 1 ? "s" : ""} de pêche
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <div className="bg-blanc border border-brume rounded-2xl p-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-brume/30 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-bretagne" />
            <Input data-testid="explorer-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className="border-0 shadow-none p-0 focus-visible:ring-0 bg-transparent text-sm" />
          </div>
          <select data-testid="filter-port" value={port} onChange={(e) => setPort(e.target.value)}
            className="bg-brume/30 rounded-xl px-3 py-2 text-sm font-semibold text-ocean focus:outline-none">
            <option value="">Tous les ports</option>
            {ports.map((p) => (<option key={p.id} value={p.name}>{p.name}</option>))}
          </select>
          <select data-testid="filter-type" value={type} onChange={(e) => setType(e.target.value)}
            className="bg-brume/30 rounded-xl px-3 py-2 text-sm font-semibold text-ocean focus:outline-none max-w-[180px]">
            <option value="">Tous types</option>
            {TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
          <select data-testid="filter-level" value={level} onChange={(e) => setLevel(e.target.value)}
            className="bg-brume/30 rounded-xl px-3 py-2 text-sm font-semibold text-ocean focus:outline-none">
            <option value="">Tous niveaux</option>
            {LEVELS.map((l) => (<option key={l} value={l}>{l}</option>))}
          </select>
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm font-semibold text-ocean px-2">
            <input data-testid="filter-has-spots" type="checkbox" checked={hasSpots}
              onChange={(e) => setHasSpots(e.target.checked)} className="w-4 h-4 accent-corail" />
            Places dispo
          </label>
          <Button data-testid="filter-reset" variant="ghost" size="sm" onClick={reset} className="text-bretagne">
            <X className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
          <Button data-testid="toggle-map" variant="ghost" size="sm" onClick={() => setShowMap(!showMap)}
            className="text-ocean ml-auto">
            <Filter className="w-3.5 h-3.5 mr-1" /> {showMap ? "Masquer la carte" : "Afficher la carte"}
          </Button>
        </div>
      </div>

      {/* Map (optional) */}
      {showMap && (
        <div className="max-w-6xl mx-auto px-6 mt-4">
          <MapView ports={ports} trips={trips} onPortClick={(p) => setPort(p.name)} height={320} />
        </div>
      )}

      {/* List */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="text-center py-16 text-bretagne">Chargement…</div>
        ) : ordered.length === 0 ? (
          <div className="text-center py-16 bg-sable border border-brume rounded-2xl">
            <div className="text-4xl">🎣</div>
            <h3 className="font-display text-xl text-ocean font-bold mt-2">Aucune sortie pour ces critères</h3>
            <p className="text-bretagne text-sm mt-1">Essayez de changer le port ou réinitialisez les filtres.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trips-grid">
            {ordered.map((t) => (<TripCard key={t.trip_id} trip={t} />))}
          </div>
        )}
      </div>
    </div>
  );
}
